import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { cn } from '@/shared/lib/cn';

/**
 * Estilo de cada elemento. O projeto não tem o plugin de tipografia do
 * Tailwind, e o reset do preflight deixa Markdown cru sem margem nem marcador
 * de lista — então cada tag recebe a classe aqui.
 *
 * ⚠️ Sem `rehype-raw`: HTML dentro do Markdown sai como texto. O conteúdo é
 * escrito (ou transcrito por IA) e lido por outra pessoa — renderizar HTML
 * cru seria XSS.
 */
const COMPONENTES: Components = {
  h1: ({ children }) => <h1 className="mt-6 mb-3 text-2xl font-bold text-brand first:mt-0">{children}</h1>,
  h2: ({ children }) => (
    <h2 className="mt-6 mb-2 border-b border-border pb-1 text-xl font-bold text-brand first:mt-0">{children}</h2>
  ),
  h3: ({ children }) => <h3 className="mt-5 mb-2 text-lg font-semibold text-foreground first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="mt-4 mb-1 font-semibold text-foreground first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="my-3 leading-relaxed text-foreground">{children}</p>,
  ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children }) => <ol className="my-3 list-decimal space-y-1 pl-6">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ children, href }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2">
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-l-4 border-brand/40 pl-4 italic text-muted-foreground">{children}</blockquote>
  ),
  code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children}</code>,
  hr: () => <hr className="my-6 border-border" />,
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-border bg-muted/50 px-3 py-1.5 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-border px-3 py-1.5 align-top">{children}</td>,
};

/** Markdown do usuário renderizado com o estilo do app (artigos do advisor). */
export function TextoMarkdown({ texto, className }: { texto: string; className?: string }) {
  return (
    <div className={cn('text-sm break-words', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTES}>
        {texto}
      </ReactMarkdown>
    </div>
  );
}
